import { Component } from '@theme/component';

/**
 * A custom element that renders a deferred video background.
 *
 * The poster remains the initial visual/LCP resource. Optimized hero instances
 * request motion only after the poster has decoded and received two paint
 * opportunities; other background videos preserve the theme's original
 * interaction-aware strategy. Exactly one suitably sized source is attached at
 * a time.
 *
 * @typedef {object} Refs
 * @property {HTMLSourceElement[]} videoSources - Deferred video source metadata.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLImageElement} [posterElement] - The visible poster image.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #armed = false;
  #loaded = false;
  #mobileDeferred = false;
  #posterReady = false;
  #paintReady = false;
  #mobileStartQueued = false;
  #paintObserver;
  #fallbackTimer;
  #mobileTimer;
  #revealTimer;
  #firstFrame;
  #secondFrame;
  #scheduleController;
  #activityController;
  #attemptController;
  #retryController;
  #candidates = [];

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('defer-load')) {
      this.#scheduleDeferredLoad();
      return;
    }

    this.#loadVideo();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelScheduledStart();
    this.#activityController?.abort();
    this.#attemptController?.abort();
    this.#retryController?.abort();
    window.clearTimeout(this.#revealTimer);

    if (!this.#loaded) this.#armed = false;
  }

  #scheduleDeferredLoad() {
    if (this.#armed || this.#loaded) return;
    this.#armed = true;

    // Respect explicit preferences for reduced data use and still imagery.
    const saveData = navigator.connection?.saveData === true;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (saveData || reducedMotion) return;

    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    this.#mobileDeferred =
      window.matchMedia('(max-width: 749px)').matches || (coarsePointer && window.innerWidth <= 1024);

    const start = () => this.#loadVideo();
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const autostartAfterPoster = this.hasAttribute('autostart-after-poster');

    if (autostartAfterPoster) {
      const schedulePosterAwareStart = () =>
        this.#schedulePosterAwareStart(start, this.#mobileDeferred ? 180 : 320);

      if (document.readyState === 'loading') {
        this.#scheduleController = new AbortController();
        document.addEventListener('DOMContentLoaded', schedulePosterAwareStart, {
          once: true,
          signal: this.#scheduleController.signal,
        });
      } else {
        schedulePosterAwareStart();
      }

      return;
    }

    this.#installActivityListeners(start, !this.#mobileDeferred && finePointer);

    if (this.#mobileDeferred) {
      const scheduleMobileStart = () => this.#schedulePosterAwareStart(start, 180);

      if (document.readyState === 'loading') {
        this.#scheduleController = new AbortController();
        document.addEventListener('DOMContentLoaded', scheduleMobileStart, {
          once: true,
          signal: this.#scheduleController.signal,
        });
      } else {
        scheduleMobileStart();
      }

      return;
    }

    const armDesktop = () => {
      if (!this.isConnected || this.#loaded) return;

      // Pointer activity normally starts the cinematic motion immediately.
      // Stationary/keyboard-only desktop visitors still receive it shortly.
      if (finePointer) this.#fallbackTimer = window.setTimeout(start, 6000);
    };

    if (document.readyState === 'complete') {
      armDesktop();
    } else {
      this.#scheduleController = new AbortController();
      window.addEventListener('load', armDesktop, {
        once: true,
        signal: this.#scheduleController.signal,
      });
    }
  }

  /**
   * Starts a deferred request only after the responsive poster and the first
   * contentful paint are ready. This prevents the video from competing with
   * critical CSS/poster bytes while still guaranteeing automatic motion.
   * @param {() => void} start
   * @param {number} quietDelay
   */
  #schedulePosterAwareStart(start, quietDelay = 180) {
    if (!this.isConnected || this.#loaded) return;

    const queueStart = () => {
      if (
        this.#mobileStartQueued ||
        !this.#posterReady ||
        !this.#paintReady ||
        !this.isConnected ||
        this.#loaded
      ) {
        return;
      }

      this.#mobileStartQueued = true;
      this.#firstFrame = window.requestAnimationFrame(() => {
        this.#secondFrame = window.requestAnimationFrame(() => {
          // Leave a short quiet window after FCP so the video request does not
          // steal bandwidth from the poster or render-blocking theme styles.
          this.#mobileTimer = window.setTimeout(start, quietDelay);
        });
      });
    };

    const markPosterReady = () => {
      if (this.#posterReady) return;
      this.#posterReady = true;
      queueStart();
    };

    const poster = this.refs.posterElement;
    if (poster instanceof HTMLImageElement) {
      const settlePoster = () => {
        const decodePromise = typeof poster.decode === 'function' ? poster.decode() : null;
        if (decodePromise instanceof Promise) {
          decodePromise.catch(() => undefined).finally(markPosterReady);
        } else {
          markPosterReady();
        }
      };

      if (poster.complete) {
        settlePoster();
      } else {
        this.#scheduleController ??= new AbortController();
        const { signal } = this.#scheduleController;
        poster.addEventListener('load', settlePoster, { once: true, signal });
        poster.addEventListener('error', markPosterReady, { once: true, signal });
      }
    } else {
      markPosterReady();
    }

    const existingPaint = performance.getEntriesByName('first-contentful-paint');
    if (existingPaint.length > 0) {
      this.#paintReady = true;
    } else if ('PerformanceObserver' in window) {
      try {
        this.#paintObserver = new PerformanceObserver((list) => {
          if (list.getEntries().some((entry) => entry.name === 'first-contentful-paint')) {
            this.#paintReady = true;
            this.#paintObserver?.disconnect();
            this.#paintObserver = undefined;
            queueStart();
          }
        });
        this.#paintObserver.observe({ type: 'paint', buffered: true });
      } catch {
        // Older engines without buffered paint observation still receive the
        // automatic bounded fallback below.
        this.#paintReady = true;
      }
    } else {
      this.#paintReady = true;
    }

    queueStart();

    // A genuine device should usually start sooner via poster + FCP. This is a
    // safety deadline only; it replaces the old interaction-only/6-second gate
    // without pushing a large video request into the earliest critical path.
    this.#fallbackTimer = window.setTimeout(start, 2400);
  }

  /**
   * Installs one-shot interaction accelerators for a deferred video.
   * @param {() => void} start
   * @param {boolean} includePointerMove
   */
  #installActivityListeners(start, includePointerMove = false) {
    this.#activityController?.abort();
    this.#activityController = new AbortController();
    const { signal } = this.#activityController;
    const passiveOnce = { once: true, passive: true, signal };

    window.addEventListener('pointerdown', start, passiveOnce);
    window.addEventListener('touchstart', start, passiveOnce);
    window.addEventListener('wheel', start, passiveOnce);
    window.addEventListener('scroll', start, passiveOnce);
    window.addEventListener('keydown', start, { once: true, signal });

    if (includePointerMove) window.addEventListener('pointermove', start, passiveOnce);
  }

  #cancelScheduledStart() {
    this.#scheduleController?.abort();
    this.#scheduleController = undefined;
    this.#paintObserver?.disconnect();
    this.#paintObserver = undefined;
    window.clearTimeout(this.#fallbackTimer);
    window.clearTimeout(this.#mobileTimer);
    window.cancelAnimationFrame(this.#firstFrame);
    window.cancelAnimationFrame(this.#secondFrame);
  }

  /**
   * Build an ordered fallback list with the most appropriate source first.
   * @returns {{url: string, width: number, mime: string}[]}
   */
  #buildCandidates() {
    const { videoSources, videoElement } = this.refs;
    if (!(videoElement instanceof HTMLVideoElement) || !videoSources?.length) return [];

    const raw = videoSources
      .map((source) => ({
        url: source.dataset.videoSource || '',
        width: Number.parseInt(source.dataset.videoWidth || '0', 10) || 0,
        mime: source.dataset.videoMime || source.type || '',
      }))
      .filter((source) => source.url)
      .filter((source) => !source.mime || videoElement.canPlayType(source.mime) !== '');

    if (!raw.length) return [];

    const mimeScore = (mime) => {
      if (/webm/i.test(mime)) return 0;
      if (/mp4/i.test(mime)) return 1;
      return 2;
    };

    if (this.#mobileDeferred) {
      // The homepage hero is full-width on mobile. Using the viewport avoids
      // a synchronous layout read during startup and is stable across paints.
      const rectWidth = Math.max(320, Math.min(window.innerWidth || 390, 749));
      const targetWidth = rectWidth <= 430 ? 480 : rectWidth <= 520 ? 540 : Math.min(720, Math.ceil(rectWidth * 1.1));

      return raw
        .sort((a, b) => {
          const score = (source) => {
            if (!source.width) return 100000 + mimeScore(source.mime);

            const oversizedTier = source.width > 720 ? 20000 + (source.width - 720) * 20 : 0;
            const undersizedTier = source.width < Math.min(rectWidth, 480) ? 10000 : 0;
            const distance = Math.abs(source.width - targetWidth) * 10;
            return oversizedTier + undersizedTier + distance + mimeScore(source.mime);
          };

          return score(a) - score(b);
        })
        .filter((candidate, index, list) => list.findIndex((item) => item.url === candidate.url) === index);
    }

    const rectWidth = this.getBoundingClientRect().width || window.innerWidth || 720;
    const targetWidth = Math.min(Math.max(Math.ceil(rectWidth * Math.min(window.devicePixelRatio || 1, 2)), 480), 1600);

    return raw
      .sort((a, b) => {
        const mimeDifference = mimeScore(a.mime) - mimeScore(b.mime);
        if (mimeDifference !== 0) return mimeDifference;

        const aWidth = a.width || Number.MAX_SAFE_INTEGER;
        const bWidth = b.width || Number.MAX_SAFE_INTEGER;
        const aDistance = aWidth >= targetWidth ? aWidth - targetWidth : targetWidth - aWidth + 5000;
        const bDistance = bWidth >= targetWidth ? bWidth - targetWidth : targetWidth - bWidth + 5000;
        return aDistance - bDistance;
      })
      .filter((candidate, index, list) => list.findIndex((item) => item.url === candidate.url) === index);
  }

  #loadVideo() {
    if (this.#loaded || !this.isConnected) return;

    const { videoElement } = this.refs;
    if (!(videoElement instanceof HTMLVideoElement)) return;

    this.#candidates = this.#buildCandidates();
    if (!this.#candidates.length) return;

    this.#loaded = true;
    this.#activityController?.abort();
    this.#cancelScheduledStart();
    this.#tryCandidate(0);
  }

  /**
   * Try one source and fall back silently if it cannot be loaded.
   * @param {number} index
   */
  #tryCandidate(index) {
    const { videoElement } = this.refs;
    const candidate = this.#candidates[index];
    if (!(videoElement instanceof HTMLVideoElement) || !candidate || !this.isConnected) {
      this.#loaded = false;
      return;
    }

    this.#attemptController?.abort();
    this.#attemptController = new AbortController();
    const { signal } = this.#attemptController;

    const markReady = () => {
      if (signal.aborted || !this.isConnected) return;
      this.#retryController?.abort();
      window.clearTimeout(this.#revealTimer);
      this.setAttribute('data-video-ready', '');
    };

    const revealFirstFrame = () => {
      if (signal.aborted || !this.isConnected) return;

      // `playing` can precede the first composited frame. Reveal only after the
      // browser confirms a video frame, with a conservative fallback for older
      // Safari versions that lack requestVideoFrameCallback.
      if (typeof videoElement.requestVideoFrameCallback === 'function') {
        videoElement.requestVideoFrameCallback(markReady);
        this.#revealTimer = window.setTimeout(markReady, 350);
      } else {
        window.requestAnimationFrame(() => window.requestAnimationFrame(markReady));
      }
    };

    const tryNext = () => {
      if (signal.aborted) return;
      this.#retryController?.abort();
      window.clearTimeout(this.#revealTimer);
      this.removeAttribute('data-video-ready');
      this.#tryCandidate(index + 1);
    };

    videoElement.addEventListener('playing', revealFirstFrame, { once: true, signal });
    videoElement.addEventListener('error', tryNext, { once: true, signal });

    // Set the autoplay-critical properties again at runtime. The HTML
    // attributes already carry the same values, but assigning the properties
    // before `play()` is more reliable in WebKit when the source is attached
    // dynamically after the initial document parse.
    videoElement.playsInline = true;
    videoElement.defaultMuted = true;
    videoElement.muted = true;
    videoElement.preload = 'auto';
    videoElement.src = candidate.url;
    videoElement.load();

    const playPromise = videoElement.play();
    if (playPromise instanceof Promise) {
      playPromise.catch(() => {
        if (signal.aborted || !this.isConnected) return;

        this.removeAttribute('data-video-ready');
        if (this.hasAttribute('defer-load') && this.#mobileDeferred) this.#armPlayRetry(signal);
      });
    }
  }

  /**
   * Retry a policy-blocked mobile hero without replacing/reloading its source.
   * @param {AbortSignal} attemptSignal
   */
  #armPlayRetry(attemptSignal) {
    const { videoElement } = this.refs;
    if (!(videoElement instanceof HTMLVideoElement) || !this.isConnected || attemptSignal.aborted) return;

    this.#retryController?.abort();
    this.#retryController = new AbortController();
    const { signal } = this.#retryController;

    const retry = () => {
      this.#retryController?.abort();
      if (attemptSignal.aborted || !this.isConnected) return;

      const playPromise = videoElement.play();
      if (playPromise instanceof Promise) {
        playPromise.catch(() => {
          if (!attemptSignal.aborted && this.isConnected) this.#armPlayRetry(attemptSignal);
        });
      }
    };

    window.addEventListener('pointerdown', retry, { once: true, passive: true, signal });
    window.addEventListener('touchstart', retry, { once: true, passive: true, signal });
    window.addEventListener('keydown', retry, { once: true, signal });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
